import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { CrewWageScale } from './useData';

export const moduleConfig = {
  module: {
    title: 'Wage scales',
    permissionModuleKey: 'crew_parameters',
    route: {
      list: '/parameters/crew?section=wage-scales',
      new: '/parameters/crew/wage-scales/new',
      detail: (id: number) => `/parameters/crew/wage-scales/${id}`,
      edit: (id: number) => `/parameters/crew/wage-scales/${id}/edit`},
    api: {
      list: '/crew-wage/scales',
      detail: (id: number) => `/crew-wage/scales/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by vessel, rank, or nationality…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: CrewWageScale) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: CrewWageScale[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New wage scale',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/crew/wage-scales/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: CrewWageScale) => item.id,
  },
  emptyStates: {
    noData: 'Add a wage scale for a vessel, rank and nationality.',
    noSearchResults: 'No wage scales match your search.',
    noFilterResults: 'No wage scales match the filters.',
  },
  delete: {
    title: 'Delete wage scale',
    message: 'Delete this wage scale?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete wage scale.',
    handler: async (id: number) => {
      const { deleteCrewWageScale } = await import('../../../services/mutations');
      await deleteCrewWageScale(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This wage scale does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Wage scale',
    getTitle: (item: CrewWageScale) => {
      const vessel = item.vessel?.vesselName ?? `Vessel #${item.vesselId}`;
      const rank = item.rank?.name ?? `Rank #${item.rankId}`;
      return `${vessel} — ${rank}`;
    }},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<CrewWageScale>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row.vessel?.vesselName} />},
  {
    key: 'rank.name',
    header: 'Rank',
    sortable: true,
    render: (_v, row) => <TextCell value={row.rank?.name} />},
  {
    key: 'nationality.name',
    header: 'Nationality',
    sortable: true,
    render: (_v, row) => <TextCell value={row.nationality?.name} />},
  {
    key: 'currency',
    header: 'Currency',
    sortable: true,
    render: (v) => <TextCell value={v} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  {
    key: 'effectiveFrom',
    header: 'From',
    sortable: true,
    render: (v) => formatDate(v)},
];

export const getDetailSections = (item: CrewWageScale, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: item.vessel?.vesselName || String(item.vesselId) },
      { label: 'Rank', value: item.rank?.name || String(item.rankId) },
      { label: 'Nationality', value: item.nationality?.name || String(item.nationalityId) },
      { label: 'Currency', value: item.currency || '' },
      { label: 'Effective from', value: formatDate(item.effectiveFrom ?? undefined) },
      { label: 'Effective to', value: formatDate(item.effectiveTo ?? undefined) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
  {
    title: 'Amounts',
    fields: (item.values ?? []).map((v) => ({
      label: v.column ? `${v.column.name}` : `Column #${v.columnId}`,
      value: String(v.amount)}))},
];

const TableActionsCell: React.FC<{
  item: CrewWageScale;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: CrewWageScale,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export type ModuleConfig = typeof moduleConfig;
